import React, { useState, useEffect } from "react";
import {
  getGruposEmpresas, getGrupoEmpresa, crearGrupoEmpresa, actualizarGrupoEmpresa,
  toggleActivoGrupoEmpresa, asociarEmpresaAGrupo, desasociarEmpresaDeGrupo, eliminarGrupoEmpresa,
  GrupoEmpresaListItem, GrupoEmpresaDetalle
} from "../../services/api";
import ConfirmarEliminacion from "../../components/ConfirmarEliminacion";

interface Props {
  usuarioId: number;
  token: string;
  onDatosCambiados?: () => void;
}

export default function GruposEmpresas({ usuarioId, token, onDatosCambiados }: Props) {
  const [grupos, setGrupos] = useState<GrupoEmpresaListItem[]>([]);
  const [cargando, setCargando] = useState(true);
  const [seleccionadoId, setSeleccionadoId] = useState<number | null>(null);
  const [detalle, setDetalle] = useState<GrupoEmpresaDetalle | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const [formNuevo, setFormNuevo] = useState({ nombre: "", descripcion: "" });

  const [editando, setEditando] = useState(false);
  const [formEditar, setFormEditar] = useState({ nombre: "", descripcion: "" });

  const [empresaAAsociar, setEmpresaAAsociar] = useState<string>("");
  const [guardando, setGuardando] = useState(false);
  const [mensajeOk, setMensajeOk] = useState("");
  const [mensajeError, setMensajeError] = useState("");
  const [confirmarEliminar, setConfirmarEliminar] = useState(false);

  const cargarGrupos = async () => {
    setCargando(true);
    const data = await getGruposEmpresas(usuarioId, token);
    setGrupos(data);
    setCargando(false);
  };

  useEffect(() => { cargarGrupos(); }, []);

  const cargarDetalle = async (id: number) => {
    setSeleccionadoId(id);
    setCargandoDetalle(true);
    setEditando(false);
    setMensajeOk("");
    setMensajeError("");
    const data = await getGrupoEmpresa(id, usuarioId, token);
    setDetalle(data);
    if (data) setFormEditar({ nombre: data.nombre, descripcion: data.descripcion || "" });
    setCargandoDetalle(false);
  };

  const mostrarError = async (mensaje: string) => {
    setMensajeError(mensaje || "Ocurrió un error");
    setTimeout(() => setMensajeError(""), 3000);
  };

  const mostrarOk = (mensaje: string) => {
    setMensajeOk(mensaje);
    setTimeout(() => setMensajeOk(""), 2000);
  };

  const handleCrear = async () => {
    if (!formNuevo.nombre.trim()) return;
    setGuardando(true);
    const res = await crearGrupoEmpresa(usuarioId, {
      nombre: formNuevo.nombre.trim(),
      descripcion: formNuevo.descripcion.trim() || undefined
    }, token);
    setGuardando(false);
    if (res.ok) {
      setMostrarNuevo(false);
      setFormNuevo({ nombre: "", descripcion: "" });
      await cargarGrupos();
      onDatosCambiados?.();
      mostrarOk("Grupo creado");
    } else {
      mostrarError(res.mensaje);
    }
  };

  const handleGuardarEdicion = async () => {
    if (!seleccionadoId || !formEditar.nombre.trim()) return;
    setGuardando(true);
    const res = await actualizarGrupoEmpresa(seleccionadoId, usuarioId, {
      nombre: formEditar.nombre.trim(),
      descripcion: formEditar.descripcion.trim() || undefined
    }, token);
    setGuardando(false);
    if (res.ok) {
      setEditando(false);
      await cargarGrupos();
      await cargarDetalle(seleccionadoId);
      onDatosCambiados?.();
      mostrarOk("Guardado correctamente");
    } else {
      mostrarError(res.mensaje);
    }
  };

  const handleToggleActivo = async (grupo: GrupoEmpresaListItem) => {
    const res = await toggleActivoGrupoEmpresa(grupo.grupoEmpresaId, usuarioId, !grupo.activo, token);
    if (res.ok) {
      await cargarGrupos();
      if (seleccionadoId === grupo.grupoEmpresaId) await cargarDetalle(grupo.grupoEmpresaId);
      onDatosCambiados?.();
    } else {
      mostrarError(res.mensaje);
    }
  };

  const handleAsociar = async () => {
    if (!seleccionadoId || !empresaAAsociar) return;
    const res = await asociarEmpresaAGrupo(seleccionadoId, Number(empresaAAsociar), usuarioId, token);
    if (res.ok) {
      setEmpresaAAsociar("");
      await cargarGrupos();
      await cargarDetalle(seleccionadoId);
      onDatosCambiados?.();
      mostrarOk("Empresa asociada");
    } else {
      mostrarError(res.mensaje);
    }
  };

  const handleEliminar = async () => {
    if (!seleccionadoId) return;
    setConfirmarEliminar(false);
    setGuardando(true);
    const res = await eliminarGrupoEmpresa(seleccionadoId, usuarioId, token);
    setGuardando(false);
    if (res.ok) {
      setSeleccionadoId(null);
      setDetalle(null);
      await cargarGrupos();
      onDatosCambiados?.();
    } else {
      mostrarError(res.mensaje);
    }
  };

  const handleDesasociar = async (empresaId: number) => {
    if (!seleccionadoId) return;
    const res = await desasociarEmpresaDeGrupo(seleccionadoId, empresaId, usuarioId, token);
    if (res.ok) {
      await cargarGrupos();
      await cargarDetalle(seleccionadoId);
      onDatosCambiados?.();
      mostrarOk("Empresa desasociada");
    } else {
      mostrarError(res.mensaje);
    }
  };

  return (
    <div style={styles.layout}>
      {/* LISTADO */}
      <div style={styles.panelLista}>
        <div style={styles.headerLista}>
          <span style={styles.titulo}>Grupos de Empresas</span>
          <button style={styles.btnPrimario} onClick={() => setMostrarNuevo(!mostrarNuevo)}>
            {mostrarNuevo ? "Cancelar" : "+ Nuevo grupo"}
          </button>
        </div>

        {mostrarNuevo && (
          <div style={styles.cajaNuevo}>
            <input
              style={styles.input}
              placeholder="Nombre del grupo"
              value={formNuevo.nombre}
              onChange={e => setFormNuevo({ ...formNuevo, nombre: e.target.value })}
            />
            <input
              style={styles.input}
              placeholder="Descripción (opcional)"
              value={formNuevo.descripcion}
              onChange={e => setFormNuevo({ ...formNuevo, descripcion: e.target.value })}
            />
            <button style={styles.btnPrimario} onClick={handleCrear} disabled={guardando || !formNuevo.nombre.trim()}>
              {guardando ? "Creando..." : "Crear"}
            </button>
          </div>
        )}

        {cargando ? (
          <div style={styles.vacio}>Cargando...</div>
        ) : grupos.length === 0 ? (
          <div style={styles.vacio}>No hay grupos creados todavía.</div>
        ) : (
          <div style={styles.lista}>
            {grupos.map(g => (
              <div
                key={g.grupoEmpresaId}
                style={{
                  ...styles.filaGrupo,
                  backgroundColor: seleccionadoId === g.grupoEmpresaId ? "#e8f4fc" : "#fff",
                  opacity: g.activo ? 1 : 0.55,
                }}
                onClick={() => cargarDetalle(g.grupoEmpresaId)}
              >
                <div>
                  <div style={styles.nombreGrupo}>{g.nombre}</div>
                  <div style={styles.subGrupo}>{g.cantidadEmpresas} empresa(s){!g.activo ? " · inactivo" : ""}</div>
                </div>
                <label style={styles.switchWrap} onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={g.activo} onChange={() => handleToggleActivo(g)} />
                </label>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DETALLE */}
      <div style={styles.panelDetalle}>
        {mensajeOk && <div style={styles.mensajeOk}>✓ {mensajeOk}</div>}
        {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}

        {!seleccionadoId && (
          <div style={styles.vacio}>Seleccioná un grupo para ver el detalle.</div>
        )}

        {seleccionadoId && cargandoDetalle && (
          <div style={styles.vacio}>Cargando...</div>
        )}

        {seleccionadoId && !cargandoDetalle && detalle && (
          <div style={styles.tarjeta}>
            <div style={styles.headerDetalle}>
              {!editando ? (
                <>
                  <div>
                    <div style={styles.tituloDetalle}>{detalle.nombre}</div>
                    <div style={styles.subtituloDetalle}>{detalle.descripcion || "Sin descripción"}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={styles.btnSecundario} onClick={() => setEditando(true)}>Editar</button>
                    <button style={styles.btnEliminar} onClick={() => setConfirmarEliminar(true)} disabled={guardando}>Eliminar</button>
                  </div>
                </>
              ) : (
                <div style={{ width: "100%" }}>
                  <input
                    style={styles.input}
                    value={formEditar.nombre}
                    onChange={e => setFormEditar({ ...formEditar, nombre: e.target.value })}
                  />
                  <input
                    style={{ ...styles.input, marginTop: 8 }}
                    placeholder="Descripción"
                    value={formEditar.descripcion}
                    onChange={e => setFormEditar({ ...formEditar, descripcion: e.target.value })}
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button style={styles.btnPrimario} onClick={handleGuardarEdicion} disabled={guardando}>
                      {guardando ? "Guardando..." : "Guardar"}
                    </button>
                    <button style={styles.btnSecundario} onClick={() => setEditando(false)}>Cancelar</button>
                  </div>
                </div>
              )}
            </div>

            <div style={styles.seccion}>
              <h4 style={styles.subTitulo}>Empresas del grupo</h4>
              {detalle.empresas.length === 0 && (
                <div style={styles.vacio}>Este grupo todavía no tiene empresas asociadas.</div>
              )}
              {detalle.empresas.map(e => (
                <div key={e.empresaId} style={styles.filaEmpresa}>
                  <span>{e.nombre}</span>
                  <button style={styles.btnQuitar} onClick={() => handleDesasociar(e.empresaId)}>Quitar</button>
                </div>
              ))}
            </div>

            <div style={styles.seccion}>
              <h4 style={styles.subTitulo}>Asociar empresa</h4>
              {detalle.empresasDisponibles.length === 0 ? (
                <div style={styles.vacio}>No hay empresas sin grupo disponibles para asociar.</div>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    style={styles.input}
                    value={empresaAAsociar}
                    onChange={e => setEmpresaAAsociar(e.target.value)}
                  >
                    <option value="">— Seleccionar empresa —</option>
                    {detalle.empresasDisponibles.map(e => (
                      <option key={e.empresaId} value={e.empresaId}>{e.nombre}</option>
                    ))}
                  </select>
                  <button style={styles.btnPrimario} onClick={handleAsociar} disabled={!empresaAAsociar}>
                    Asociar
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {confirmarEliminar && detalle && (
        <ConfirmarEliminacion
          titulo={`Eliminar grupo "${detalle.nombre}"`}
          nombreEsperado={detalle.nombre}
          onConfirmar={handleEliminar}
          onCancelar={() => setConfirmarEliminar(false)}
        />
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  layout: { display: "flex", gap: 20, alignItems: "flex-start" },
  panelLista: { flex: "0 0 340px", backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: 20 },
  headerLista: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 },
  titulo: { fontSize: 16, fontWeight: 700, color: "#1a1a2e" },
  cajaNuevo: { display: "flex", flexDirection: "column", gap: 8, backgroundColor: "#f8f9fa", borderRadius: 8, padding: 12, marginBottom: 14 },
  lista: { display: "flex", flexDirection: "column", gap: 6, maxHeight: 520, overflowY: "auto" as const },
  filaGrupo: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderRadius: 8, border: "1px solid #eee", cursor: "pointer" },
  nombreGrupo: { fontSize: 13, fontWeight: 600, color: "#1a1a2e" },
  subGrupo: { fontSize: 11, color: "#999", marginTop: 2 },
  switchWrap: { display: "flex", alignItems: "center" },
  panelDetalle: { flex: 1, minWidth: 0 },
  tarjeta: { backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: 24 },
  headerDetalle: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "1px solid #f0f0f0", paddingBottom: 16, marginBottom: 16 },
  tituloDetalle: { fontSize: 17, fontWeight: 700, color: "#1a1a2e" },
  subtituloDetalle: { fontSize: 13, color: "#888", marginTop: 2 },
  seccion: { marginTop: 20 },
  subTitulo: { fontSize: 12, fontWeight: 700, color: "#999", textTransform: "uppercase" as const, letterSpacing: "0.5px", marginBottom: 10 },
  filaEmpresa: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", backgroundColor: "#f8f9fa", borderRadius: 8, marginBottom: 6, fontSize: 13, color: "#333" },
  input: { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, boxSizing: "border-box" as const, outline: "none" },
  btnPrimario: { padding: "8px 16px", backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" as const },
  btnSecundario: { padding: "8px 16px", backgroundColor: "#f4f6f8", color: "#555", border: "1px solid #ddd", borderRadius: 8, fontSize: 13, cursor: "pointer" },
  btnQuitar: { padding: "5px 10px", backgroundColor: "#fff0f0", color: "#e74c3c", border: "1px solid #f5c6c6", borderRadius: 6, fontSize: 12, cursor: "pointer" },
  btnEliminar: { padding: "8px 16px", backgroundColor: "#fff0f0", color: "#e74c3c", border: "1px solid #f5c6c6", borderRadius: 8, fontSize: 13, cursor: "pointer" },
  vacio: { padding: 20, textAlign: "center" as const, color: "#bbb", fontSize: 13 },
  mensajeOk: { backgroundColor: "#e8f8f0", color: "#27ae60", padding: "10px 16px", borderRadius: 8, fontSize: 13, marginBottom: 12 },
  mensajeError: { backgroundColor: "#fff0f0", color: "#e74c3c", padding: "10px 16px", borderRadius: 8, fontSize: 13, marginBottom: 12 },
};
